import * as React from 'react';

import type {
  DragCancelEvent,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  DraggableAttributes,
} from '@dnd-kit/core';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@rematter/ui-web';
import { useAtom } from 'jotai';

import { InventoryItemButton } from '../InventoryItemButton';

import { EditCardOverlays } from './EditCardOverlays';
import { updateFavoriteOrdering } from './favoriteOrdering';
import { orderEntitiesByIds, updateOrderedIds } from './orderedIds';

import { HueColorPicker } from '~/modules/trade/_components/scale-ticket-pro/shared/HueColorPicker';
import {
  $touchModalColorChanges,
  $touchModalGroupIds,
  $touchModalGroupOrderIds,
  type TouchModalColorChange,
} from '~/modules/trade/_state/scaleTicketSettings.state';
import { useInventoryGroupStyle } from '~/modules/trade/hooks/useInventoryGroupStyle';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { InventoryItemRecord } from '~/modules/trade/types/load';
import { usePermission } from '~/shared/hooks/usePermission';

type EditModeRootViewProps = {
  itemGroups: InventoryItemRecord[];
  searchTerm: string;
  onSelectGroup: (groupId: string) => void;
};

//
// ⚛️ ⚛️ ⚛️ EDIT GROUP CARD ⚛️ ⚛️ ⚛️
//
type EditGroupCardProps = {
  group: InventoryItemRecord;
  colorHue: number | null | undefined;
  isFavorite: boolean;
  canEditColors: boolean;
  onNavigate: () => void;
  onToggleFavorite: () => void;
  onColorSwatchPress: () => void;
  sortableRef?: (node: HTMLElement | null) => void;
  sortableStyle?: React.CSSProperties;
  dragAttributes?: DraggableAttributes;
  dragListeners?: ReturnType<typeof useSortable>['listeners'];
  className?: string;
};

const EditGroupCard = ({
  group,
  colorHue,
  isFavorite,
  canEditColors,
  onNavigate,
  onToggleFavorite,
  onColorSwatchPress,
  sortableRef,
  sortableStyle,
  dragAttributes,
  dragListeners,
  className,
}: EditGroupCardProps) => {
  const overrideStyle = useInventoryGroupStyle({
    colorHue: colorHue ?? undefined,
  });

  return (
    <div
      ref={sortableRef}
      style={sortableStyle}
      className={cn('@EditGroupCard relative w-full', className)}
    >
      <InventoryItemButton
        inventoryItem={group}
        onPress={onNavigate}
        className="!w-full"
        style={overrideStyle}
      />

      <EditCardOverlays
        isFavorite={isFavorite}
        canEditColors={canEditColors}
        colorHue={colorHue}
        onToggleFavorite={onToggleFavorite}
        onColorSwatchPress={onColorSwatchPress}
        dragAttributes={dragAttributes}
        dragListeners={dragListeners}
      />
    </div>
  );
};

//
// ⚛️ ⚛️ ⚛️ SORTABLE EDIT GROUP CARD ⚛️ ⚛️ ⚛️
//
const SortableEditGroupCard = (
  props: Omit<
    React.ComponentProps<typeof EditGroupCard>,
    'sortableRef' | 'sortableStyle' | 'dragAttributes' | 'dragListeners'
  > & { id: string },
) => {
  const { id, ...rest } = props;
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  return (
    <EditGroupCard
      {...rest}
      sortableRef={setNodeRef}
      sortableStyle={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      dragAttributes={attributes}
      dragListeners={listeners}
      className="@SortableEditGroupCard"
    />
  );
};

//
// ⚛️ ⚛️ ⚛️ EDIT MODE ROOT VIEW ⚛️ ⚛️ ⚛️
//
export const EditModeRootView = ({
  itemGroups,
  searchTerm,
  onSelectGroup,
}: EditModeRootViewProps) => {
  const t = useTranslations();

  const [groupIds, setGroupIds] = useAtom($touchModalGroupIds);
  const [groupOrderIds, setGroupOrderIds] = useAtom($touchModalGroupOrderIds);
  const [colorChanges, setColorChanges] = useAtom($touchModalColorChanges);

  const { allowed: canEditColors } = usePermission('itemGroupUpdate');

  const [colorPickerGroupId, setColorPickerGroupId] = React.useState<
    string | null
  >(null);
  const dragSnapshotRef = React.useRef<{
    groupIds: string[];
    groupOrderIds: string[];
  } | null>(null);
  const lastDragTargetRef = React.useRef<string | null>(null);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 5 },
    }),
    useSensor(KeyboardSensor),
  );

  const groupsById = React.useMemo(
    () => new Map(itemGroups.map((g) => [g.id, g])),
    [itemGroups],
  );
  const effectiveGroupOrderIds = React.useMemo(
    () =>
      groupOrderIds.length > 0
        ? [...groupOrderIds, ...itemGroups.map((group) => group.id)].filter(
            (id, index, ids) => ids.indexOf(id) === index && groupsById.has(id),
          )
        : itemGroups.map((group) => group.id),
    [groupOrderIds, itemGroups, groupsById],
  );
  const orderedGroups = React.useMemo(
    () => orderEntitiesByIds(itemGroups, effectiveGroupOrderIds),
    [itemGroups, effectiveGroupOrderIds],
  );
  const groupIdsRef = React.useRef(groupIds);
  const effectiveGroupOrderIdsRef = React.useRef(effectiveGroupOrderIds);

  React.useEffect(() => {
    groupIdsRef.current = groupIds;
  }, [groupIds]);

  React.useEffect(() => {
    effectiveGroupOrderIdsRef.current = effectiveGroupOrderIds;
  }, [effectiveGroupOrderIds]);

  const favoriteGroupIds = React.useMemo(() => {
    const groupSet = new Set(itemGroups.map((g) => g.id));
    return groupIds.filter((id) => groupSet.has(id));
  }, [groupIds, itemGroups]);

  const favoriteSet = React.useMemo(
    () => new Set(favoriteGroupIds),
    [favoriteGroupIds],
  );

  const nonFavoriteGroups = React.useMemo(
    () => orderedGroups.filter((g) => !favoriteSet.has(g.id)),
    [orderedGroups, favoriteSet],
  );

  const filteredFavoriteIds = React.useMemo(() => {
    if (!searchTerm) return favoriteGroupIds;
    const term = searchTerm.toLowerCase();
    return favoriteGroupIds.filter((id) => {
      const g = groupsById.get(id);
      return g?.name.toLowerCase().includes(term);
    });
  }, [favoriteGroupIds, searchTerm, groupsById]);

  const filteredNonFavorites = React.useMemo(() => {
    if (!searchTerm) return nonFavoriteGroups;
    const term = searchTerm.toLowerCase();
    return nonFavoriteGroups.filter((g) => g.name.toLowerCase().includes(term));
  }, [nonFavoriteGroups, searchTerm]);

  const getEffectiveColorHue = React.useCallback(
    (groupId: string): number | null => {
      const change = colorChanges[groupId];
      if (change?.colorHue != null) return change.colorHue;
      return groupsById.get(groupId)?.colorHue ?? null;
    },
    [colorChanges, groupsById],
  );

  const handleToggleFavorite = React.useCallback(
    (groupId: string) => {
      if (favoriteSet.has(groupId)) {
        setGroupIds(groupIds.filter((id) => id !== groupId));
      } else {
        setGroupIds([...groupIds, groupId]);
      }
    },
    [groupIds, favoriteSet, setGroupIds],
  );

  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) {
        if (!over && dragSnapshotRef.current) {
          setGroupIds(dragSnapshotRef.current.groupIds);
          setGroupOrderIds(dragSnapshotRef.current.groupOrderIds);
        }
        dragSnapshotRef.current = null;
        lastDragTargetRef.current = null;
        return;
      }
      dragSnapshotRef.current = null;
      lastDragTargetRef.current = null;
    },
    [setGroupIds, setGroupOrderIds],
  );

  const handleDragStart = React.useCallback((_event: DragStartEvent) => {
    dragSnapshotRef.current = {
      groupIds: groupIdsRef.current,
      groupOrderIds: effectiveGroupOrderIdsRef.current,
    };
    lastDragTargetRef.current = null;
  }, []);

  const handleDragOver = React.useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const overId = String(over.id);
      if (lastDragTargetRef.current === `${active.id}:${overId}`) return;
      lastDragTargetRef.current = `${active.id}:${overId}`;

      const nextGroupIds = updateFavoriteOrdering(
        groupIdsRef.current,
        String(active.id),
        overId,
      );
      const nextGroupOrderIds = updateOrderedIds(
        effectiveGroupOrderIdsRef.current,
        String(active.id),
        overId,
      );

      groupIdsRef.current = nextGroupIds;
      effectiveGroupOrderIdsRef.current = nextGroupOrderIds;
      setGroupIds(nextGroupIds);
      setGroupOrderIds(nextGroupOrderIds);
    },
    [setGroupIds, setGroupOrderIds],
  );

  const handleDragCancel = React.useCallback(
    (_event: DragCancelEvent) => {
      if (dragSnapshotRef.current) {
        setGroupIds(dragSnapshotRef.current.groupIds);
        setGroupOrderIds(dragSnapshotRef.current.groupOrderIds);
      }
      dragSnapshotRef.current = null;
      lastDragTargetRef.current = null;
    },
    [setGroupIds, setGroupOrderIds],
  );

  const handleColorChange = React.useCallback(
    (groupId: string, hue: number | null) => {
      setColorChanges((prev: Record<string, TouchModalColorChange>) => ({
        ...prev,
        [groupId]: { type: 'group', colorHue: hue },
      }));
    },
    [setColorChanges],
  );

  const handleColorSwatchPress = React.useCallback((groupId: string) => {
    setColorPickerGroupId((prev) => (prev === groupId ? null : groupId));
  }, []);

  const colorPickerGroup = colorPickerGroupId
    ? groupsById.get(colorPickerGroupId)
    : null;

  return (
    <div className="@EditModeRootView flex flex-col gap-6">
      {(filteredFavoriteIds.length > 0 || filteredNonFavorites.length > 0) && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          {/* ★ FAVORITES */}
          {filteredFavoriteIds.length > 0 && (
            <div className="flex flex-col gap-4">
              <span className="text-xs font-medium text-foreground/50 uppercase">
                {t.scaleTicketPro.settings.favorites}
              </span>
              <SortableContext
                items={filteredFavoriteIds}
                strategy={horizontalListSortingStrategy}
              >
                <div className="grid grid-cols-4 gap-2">
                  {filteredFavoriteIds.map((id) => {
                    const group = groupsById.get(id);
                    if (!group) return null;
                    return (
                      <SortableEditGroupCard
                        key={id}
                        id={id}
                        group={group}
                        colorHue={getEffectiveColorHue(id)}
                        isFavorite={true}
                        canEditColors={canEditColors}
                        onNavigate={() => onSelectGroup(id)}
                        onToggleFavorite={() => handleToggleFavorite(id)}
                        onColorSwatchPress={() => handleColorSwatchPress(id)}
                      />
                    );
                  })}
                </div>
              </SortableContext>
            </div>
          )}

          {filteredFavoriteIds.length > 0 &&
            filteredNonFavorites.length > 0 && (
              <hr className="m-0 border-t-2 border-neutral/15" />
            )}

          {/* ALL GROUPS */}
          {filteredNonFavorites.length > 0 && (
            <div className="flex flex-col gap-4">
              <span className="text-xs font-medium text-foreground/50 uppercase">
                {t.scaleTicketPro.settings.allGroups}
              </span>
              <SortableContext
                items={filteredNonFavorites.map((group) => group.id)}
                strategy={horizontalListSortingStrategy}
              >
                <div className="grid grid-cols-4 gap-2">
                  {filteredNonFavorites.map((group) => (
                    <SortableEditGroupCard
                      key={group.id}
                      id={group.id}
                      group={group}
                      colorHue={getEffectiveColorHue(group.id)}
                      isFavorite={false}
                      canEditColors={canEditColors}
                      onNavigate={() => onSelectGroup(group.id)}
                      onToggleFavorite={() => handleToggleFavorite(group.id)}
                      onColorSwatchPress={() =>
                        handleColorSwatchPress(group.id)
                      }
                    />
                  ))}
                </div>
              </SortableContext>
            </div>
          )}
        </DndContext>
      )}

      {/* INLINE COLOR PICKER */}
      {canEditColors && colorPickerGroup && (
        <div className="rounded-lg border border-foreground/10 p-3">
          <div className="mb-2 text-sm font-medium">
            {colorPickerGroup.name}
          </div>
          <HueColorPicker
            value={
              colorChanges[colorPickerGroup.id]?.colorHue ??
              colorPickerGroup.colorHue ??
              null
            }
            onChange={(hue) => handleColorChange(colorPickerGroup.id, hue)}
          />
        </div>
      )}
    </div>
  );
};
